import React from "react";
import { Filter } from "lucide-react";
import { cn } from "../../../lib/utils.new";
import { SearchDisplayMode, SearchType } from "../../types/TCanvasOptions";
import {
  SearchEventType,
  ResultDataKeys,
  SearchEvent,
} from "../../types/SearchEvent";
import { Button } from "../../../components/ui/button";
import { ResultData } from "../../types/SearchEvent";
import { Progress } from "../../../components/ui/progress";
import { SocketsContext } from "../../context/SocketProvider";
import useContext from "../../../hooks/useContext";
import { CurrSubscriptionContext } from "../../../context/CurrSubscriptionProvider";
import { TransitionContext } from "../../context/TransitionProvider";
import SemanticSearchInitialization from "./SemanticSearchInitialization";
import { getCheckSemanticSearchInitialization } from "../../utils/api";

interface IProps {
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  inputSearchText: string;
  setInputSearchText: React.Dispatch<React.SetStateAction<string>>;
  openSelectionFilter: boolean;
  setOpenSelectionFilter: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterItems: React.Dispatch<React.SetStateAction<number[] | null>>;
  searchDisplayMode: SearchDisplayMode;
  setSearchDisplayMode: React.Dispatch<React.SetStateAction<SearchDisplayMode>>;
}

export default function SearchBar({
  searchType,
  inputSearchText,
  setInputSearchText,
  openSelectionFilter,
  setOpenSelectionFilter,
  setFilterItems,
  setSearchDisplayMode,
}: IProps) {
  const [local_input, set_local_input] = React.useState<string>("");

  const [all_search_results, set_all_search_results] =
    React.useState<ResultData | null>(null);
  const [buckets, set_buckets] = React.useState<[boolean, boolean]>([
    true,
    false,
  ]);
  const [progress, set_progress] = React.useState<number | null>(null);
  const { currentSub: subscription } = useContext(CurrSubscriptionContext);
  const { id: subscription_id } = subscription ?? {};
  const { socket } = useContext(SocketsContext);
  const { startTransition } = useContext(TransitionContext);
  const [isLoading, setIsLoading] = React.useState<number>(0);
  const [
    semanticSearchInitializationState,
    setSemanticSearchInitializationState,
  ] = React.useState<"OPEN" | "RUNNING" | "ERROR" | "SUCCESS">();
  const [openSemanticSearchInitializer, setOpenSemanticSearchInitializer] =
    React.useState<boolean>(false);

  const handleOpenSelection = () => {
    setOpenSelectionFilter((prev) => !prev);
  };

  const checkIsSemanticSearchInitialized = async () => {
    return getCheckSemanticSearchInitialization().then((data) => {
      setSemanticSearchInitializationState(data);
      return data;
    });
  };

  React.useEffect(() => {
    checkIsSemanticSearchInitialized();
  }, []);

  React.useEffect(() => {
    set_buckets([true, false]);
    set_all_search_results(null);
    setInputSearchText("");
    set_local_input("");
    setIsLoading(0);
    set_progress(null);
    setFilterItems(null);
  }, [searchType]);

  React.useEffect(() => {
    if (all_search_results == null) {
      setFilterItems(null);
      return;
    }
    let final_list = [];
    if (buckets[0]) {
      let temp = all_search_results[ResultDataKeys.HIGH] ?? [];
      final_list.push(...temp);
    }
    if (buckets[1]) {
      let temp = all_search_results[ResultDataKeys.LOW] ?? [];
      final_list.push(...temp);
    }
    setFilterItems(final_list);
  }, [all_search_results, buckets]);

  const handleSearch = async () => {
    if (subscription_id == undefined) return;
    if (searchType == SearchType.SEMANTIC) {
      if (semanticSearchInitializationState != "SUCCESS") {
        let data = await checkIsSemanticSearchInitialized();
        if (data != "SUCCESS") {
          setOpenSemanticSearchInitializer(true);
          return;
        }
      }
      if (local_input.trim() == "") {
        set_all_search_results(null);
      } else {
        set_all_search_results(null);

        setIsLoading(5);
        socket.current?.emit("search", {
          subscription_id: subscription_id,
          query: local_input,
        });

        const handleSearchResults = (data: SearchEvent) => {
          if (data.event_type == SearchEventType.UNRANKED) {
            if (
              Object.keys(data.data).length == 0 ||
              (data.data[ResultDataKeys.HIGH] == undefined &&
                data.data[ResultDataKeys.LOW] == undefined)
            ) {
              setSearchDisplayMode(SearchDisplayMode.FILTER);
              set_all_search_results(data.data);
              set_progress(null);
              socket.current?.off("search_results");
            } else {
              set_all_search_results(data.data);
              startTransition();
            }
            setIsLoading(40);
          } else if (data.event_type == SearchEventType.PROGRESS) {
            set_progress((data.data.completed / data.data.total) * 100);
          } else {
            setSearchDisplayMode(SearchDisplayMode.FILTER);
            set_all_search_results(data.data);
            socket.current?.off("search_results");
          }
        };
        socket.current?.on("search_results", handleSearchResults);
      }
    } else if (searchType == SearchType.NORMAL) {
      if (local_input.trim() == "") {
        setInputSearchText("");
      } else if (local_input != inputSearchText) {
        setInputSearchText(local_input);
      }
    }
  };

  const toggleBucket = (index: number) => {
    return () =>
      set_buckets((prev) => {
        let newPrev: [boolean, boolean] = [prev[0], prev[1]];
        newPrev[index] = !newPrev[index];
        return newPrev;
      });
  };

  const onLocalInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const new_value = event.target.value;
    set_local_input(new_value);
    if (new_value.trim() == "") {
      setSearchDisplayMode(SearchDisplayMode.HIGHLIGHT);
      setInputSearchText("");
      set_all_search_results(null);
      setIsLoading(0);
      set_progress(null);
      set_buckets([true, false]);
    }

    setIsLoading(0);
    set_progress(null);
  };

  return (
    <div>
      <div className="flex flex-row items-stretch w-full gap-2">
        <button
          className="bg-slate-900/90 border border-blue-500/20 rounded-lg align-middle px-3 items-center h-auto hover:bg-slate-800/90 hover:border-blue-500/40 transition-all duration-200 shadow-lg shadow-blue-500/10 backdrop-blur-sm"
          onClick={handleOpenSelection}
        >
          <Filter
            className={cn(
              "w-5 h-5 transition-all duration-300 ease-out",
              openSelectionFilter ? "text-blue-400 drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]" : "text-slate-400",
            )}
          />
        </button>
        <div className="relative w-full h-fit">
          <div className="relative w-full bg-slate-900/90 border-blue-500/20 border rounded-lg flex justify-start items-center overflow-hidden shadow-lg shadow-blue-500/10 backdrop-blur-sm hover:border-blue-500/30 transition-all duration-200">
            {
              <div className="absolute flex items-center justify-start w-full h-full">
                <Progress
                  value={isLoading + (progress ? progress * 0.6 : 0)}
                  className="w-full h-full rounded-none bg-transparent"
                  progressColor="bg-blue-500/20"
                />
              </div>
            }
            <div className="relative p-1 px-4 z-1 w-full">
              <input
                className="w-full p-2 bg-transparent border-0 outline-none focus:outline-none text-slate-100 placeholder:text-slate-500 focus:border-0 focus:ring-0 text-sm"
                value={local_input}
                onChange={onLocalInputChange}
                onKeyDown={(event) => {
                  if (event.key == "Enter") {
                    handleSearch();
                  }
                }}
                placeholder={
                  searchType == SearchType.SEMANTIC
                    ? "Semantic Search..."
                    : "Search nodes..."
                }
              />
              <SemanticSearchInitialization
                semanticSearchInitializationState={
                  semanticSearchInitializationState
                }
                setSemanticSearchInitializationState={
                  setSemanticSearchInitializationState
                }
                open={openSemanticSearchInitializer}
                setOpen={setOpenSemanticSearchInitializer}
              />
            </div>
          </div>
          {searchType == SearchType.SEMANTIC && all_search_results != null && (
            <div className="flex justify-end gap-2 pt-2">
              <Button
                className={cn(
                  "flex justify-between border rounded-lg text-[10px] font-semibold relative text-left px-3 py-2 flex-1 transition-all duration-200 shadow-md",
                  buckets[0]
                    ? "bg-blue-600 border-blue-500 text-white hover:bg-blue-700 shadow-blue-500/30"
                    : "bg-slate-900/90 border-blue-500/20 text-slate-400 hover:border-blue-500/40 hover:text-blue-400 hover:bg-slate-800/90 shadow-blue-500/10",
                )}
                onClick={toggleBucket(0)}
              >
                <p>HIGH</p>
                <p className="ml-2">{all_search_results[ResultDataKeys.HIGH]?.length}</p>
              </Button>
              <Button
                className={cn(
                  "flex justify-between border rounded-lg text-[10px] font-semibold relative text-left px-3 py-2 flex-1 transition-all duration-200 shadow-md",
                  buckets[1]
                    ? "bg-blue-600 border-blue-500 text-white hover:bg-blue-700 shadow-blue-500/30"
                    : "bg-slate-900/90 border-blue-500/20 text-slate-400 hover:border-blue-500/40 hover:text-blue-400 hover:bg-slate-800/90 shadow-blue-500/10",
                )}
                onClick={toggleBucket(1)}
              >
                <p>LOW</p>
                <p className="ml-2">{all_search_results[ResultDataKeys.LOW]?.length}</p>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
